import { SliceZone } from '@prismicio/react';
import { Radio } from '@remote-com/mushu/src/components/Ui/Form/Radio/Radio';
import { RadioState } from '@remote-com/mushu/src/components/Ui/Form/Radio/Radio.styled';
import UserIcon from '@remote-com/norma/icons/user.svg';
import { Formik, Form } from 'formik';
import Image from 'next/image';
import { Elements, RichText } from 'prismic-reactjs';
import { useState, createElement, useEffect } from 'react';
import { Button, ButtonInline } from 'remote-com-shared/components/Button';
import { PrimaryCTA } from 'remote-com-shared/components/CallToAction';
import { PrimaryCTAButton } from 'remote-com-shared/components/CallToAction/Primary';
import { ClientLogosSlider } from 'remote-com-shared/components/ClientLogos/ClientLogosSlider';
import { DocumentHead } from 'remote-com-shared/components/DocumentHead';
import { Hero, ImageBackground } from 'remote-com-shared/components/Hero';
import { MarketingBannerSlice } from 'remote-com-shared/components/MarketingBanners/MarketingBannerSlice';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import { ExploreBenefitsSection } from './ExploreBenefitsSection';
import { HowWeHelpSection } from './HowWeHelpSection';
import { RelocationInterestFormModal } from './RelocationInterestForm';
import { StepsProgressBar } from './StepsProgressBar';
import { WhyRemoteSection } from './WhySection';
import { CountryCardsSection } from './WorkAnywhere';
import HeroBackgroundImage from './assets/3d-hero-bg.png';
import BuildingIcon from './assets/building.svg';
import CTALeftillustration from './assets/cta-left-illustration.png';

import { FAQSection } from '@/src/components/FrequentlyAskedQuestions';
import { Testimonials } from '@/src/components/Testimonials';
import { RemoteHelpSection } from '@/src/domains/RemoteHelpRefugees';
import { BillingFrequencyControls } from '@/src/domains/pricing/PricingOverviewSection';
import { tabletBreakpoint, desktopBreakpoint } from '@/src/domains/shared/breakpoints';

export const RadioButtonContent = styled.div`
  display: flex;
  margin-left: -15px;
  align-items: center;
  color: var(--colors-moonRaker);

  ${({ $active }) => {
    switch ($active) {
      case true:
        return css`
          opacity: 1;
        `;
      case false:
      default:
        return css`
          opacity: 0.5;
        `;
    }
  }}
  svg {
    margin-right: 7px;
    fill: var(--colors-moonRaker);
  }
`;

export const EmploymentControls = styled(BillingFrequencyControls)`
  ${RadioState} {
    border-color: ${({ theme }) => theme.colors.irisBlue_45};
    margin-left: 0;

    &:hover {
      border-color: ${({ theme }) => theme.colors.irisBlue_45};
    }
  }
`;

export const FormWrapper = styled(Form)`
  display: flex;
`;

export const CustomRadio = styled(Radio)`
  align-items: center;
`;

const HeroBackground = styled(ImageBackground)`
  right: 0;
  bottom: 0;
  display: flex;
  max-width: 50%;

  ${tabletBreakpoint(css`
    display: none;
  `)}
`;

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ActionsCTA = styled.div`
  flex-direction: row;
`;

export const BottomActionsCTA = styled(ActionsCTA)`
  margin-left: 0;
  display: flex;
  margin-top: 56px;
`;

export const CTABottomWrapper = styled.div`
  position: relative;
`;

export const IllustrationWrapper = styled.div`
  position: absolute;
  left: 0;
  ${fluidProp('top', '-75px', '-125px')}
  ${fluidProp('width', '55px', '272px')}

    ${desktopBreakpoint(css`
    display: none;
  `)}
`;

export const HeroPrimaryCTAButton = styled(Button)`
  && {
    ${fluidProp(['padding-left', 'padding-right'], '30px', '32px')}
  }
`;

export function RemoteRelocationView({
  employeeHubspotHeroFormDefinition,
  employerHubspotHeroFormDefinition,
  clientTestimonialsView,
  remoteRelocationView,
  helpUkraineOverview,
  clientLogosView,
}) {
  const {
    hero_title: heroTitle,
    hero_description: heroDescription,
    hero_cta_label: heroCtaLabel,
    logos_label: logosLabel,
    logos_title: logosTitle,
    frequently_asked_questions: frequentlyAskedQuestions,
    explore_label: exploreLabel,
    explore_title: exploreTitle,
    explore_description: exploreDescription,
    explore_sections: exploreSections,
    progress_label: progressLabel,
    progress_title: progressTitle,
    progress_steps: progressSteps,
    progress_employee_steps: progressEmployeeSteps,
    how_label: howLabel,
    how_title: howTitle,
    how_description: howDescription,
    how_cards: howCards,
    overview_label: overviewLabel,
    overview_title: overviewTitle,
    overview_description: overviewDescription,
    overview_cards: overviewCards,
    flag_label: flagLabel,
    flag_title: flagTitle,
    flag_description: flagDescription,
    flags,
    globe_section: globeSection,
    work_label: workLabel,
    work_title: workTitle,
    work_description: workDescription,
    work_bottom_description: workBottomDescription,
    work_bottom_title: workBottomTitle,
    work_cards: workCards,
    country_cards: countryCards,
    body,
  } = remoteRelocationView;

  const [displayModal, setDisplayModal] = useState(false);
  const [employmentValue, setEmploymentValue] = useState('Employer');

  function openModal() {
    setDisplayModal(true);
  }
  function closeModal() {
    setDisplayModal(false);
  }

  useEffect(() => {
    if (window?.location?.search) {
      const toggleIfParam = new URLSearchParams(window.location.search).get(
        'remote-relocation-interest'
      );
      setDisplayModal(toggleIfParam);
    }
  }, []);

  function htmlSerializer(type, element, children, key) {
    switch (type) {
      case Elements.hyperlink: {
        const link = element.data.url;
        const props = element.data.target ? { target: element.data.target } : {};
        if (link === 'https://remote-relocation-interest-form') {
          return createElement(
            ButtonInline,
            Object.assign(props, {
              key,
              onClick(event) {
                event.preventDefault();
                openModal();
              },
            }),
            children
          );
        }
        return (
          <ButtonInline {...props} href={link}>
            {children}
          </ButtonInline>
        );
      }
      default:
        return null;
    }
  }

  return (
    <>
      <DocumentHead document={remoteRelocationView} />
      <Hero
        title={<RichText render={heroTitle} />}
        description={<RichText render={heroDescription} />}
        background={
          <HeroBackground as="div">
            <Image src={HeroBackgroundImage} quality="100" width="600px" height="784px" alt="" />
          </HeroBackground>
        }
        actions={
          <ActionsWrapper>
            <ActionsCTA>
              <HeroPrimaryCTAButton onClick={openModal}>{heroCtaLabel}</HeroPrimaryCTAButton>
            </ActionsCTA>
            <BottomActionsCTA>
              {' '}
              <EmploymentControls>
                <Formik initialValues={{ employmentValue }}>
                  {({ handleChange }) => (
                    <FormWrapper>
                      <CustomRadio
                        name="employmentValue"
                        value="Employer"
                        label={
                          <RadioButtonContent $active={employmentValue === 'Employer'}>
                            <BuildingIcon />
                            Employer
                          </RadioButtonContent>
                        }
                        onChange={(event) => {
                          handleChange(event);
                          setEmploymentValue(event.target.value);
                        }}
                      />

                      <CustomRadio
                        name="employmentValue"
                        value="Employee"
                        label={
                          <RadioButtonContent $active={employmentValue === 'Employee'}>
                            <UserIcon />
                            Employee
                          </RadioButtonContent>
                        }
                        onChange={(event) => {
                          handleChange(event);
                          setEmploymentValue(event.target.value);
                        }}
                      />
                    </FormWrapper>
                  )}
                </Formik>
              </EmploymentControls>
            </BottomActionsCTA>
          </ActionsWrapper>
        }
        hideOverflow
        preview="0vh"
      />
      <RemoteHelpSection pageView={helpUkraineOverview} />
      <CountryCardsSection
        label={workLabel}
        title={workTitle}
        description={workDescription}
        bottomTitle={workBottomTitle}
        bottomDescription={workBottomDescription}
        list={workCards}
        countryCards={countryCards}
        employmentValue={employmentValue}
      />

      <SliceZone
        slices={body}
        components={{ banner: MarketingBannerSlice }}
        context={{ remoteRelocationView, reducedSpacing: true }}
      />

      {employmentValue === 'Employee' && (
        <>
          <WhyRemoteSection
            label={overviewLabel}
            title={overviewTitle}
            description={overviewDescription}
            list={overviewCards}
            flagLabel={flagLabel}
            flagTitle={flagTitle}
            flagDescription={flagDescription}
            globeSection={globeSection}
            htmlSerializer={htmlSerializer}
            form={remoteRelocationView}
            hubspotFormDefinition={employeeHubspotHeroFormDefinition}
            employmentValue={employmentValue}
            flags={flags}
          />
        </>
      )}
      {employmentValue === 'Employer' && (
        <>
          <ExploreBenefitsSection
            label={exploreLabel ?? 'Why Remote'}
            title={exploreTitle}
            description={exploreDescription}
            list={exploreSections}
          />
          <HowWeHelpSection
            label={howLabel}
            title={howTitle}
            description={howDescription}
            list={howCards}
          />
        </>
      )}
      <StepsProgressBar
        steps={employmentValue === 'Employer' ? progressSteps : progressEmployeeSteps}
        title={progressTitle}
        label={progressLabel}
        align="left"
      />

      <ClientLogosSlider label={logosLabel} title={logosTitle} {...clientLogosView} />
      <Testimonials {...clientTestimonialsView} />
      <FAQSection
        frequentlyAskedQuestions={frequentlyAskedQuestions}
        htmlSerializer={htmlSerializer}
      />
      <CTABottomWrapper>
        <PrimaryCTA $noIllustration title={<>See how easy global relocation can be.</>}>
          <PrimaryCTAButton onClick={openModal}>Let&apos;s connect</PrimaryCTAButton>
        </PrimaryCTA>
        <IllustrationWrapper>
          <Image src={CTALeftillustration} alt="" />
        </IllustrationWrapper>
      </CTABottomWrapper>
      <RelocationInterestFormModal
        form={remoteRelocationView}
        hubspotFormDefinition={
          employmentValue === 'Employer'
            ? employerHubspotHeroFormDefinition
            : employeeHubspotHeroFormDefinition
        }
        visible={displayModal}
        onCancel={closeModal}
        employmentValue={employmentValue}
      />
    </>
  );
}
